import type { ShareScope } from '@mathflat/domain/@entities/(Content)/constants'
import type { WorkbookApi } from '@mathflat/domain/@entities/(Content)/Workbook/api'
import { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import type { ProblemApi } from '@mathflat/domain/@entities/Problem/api'
import { action, observable } from 'mobx'
import { generatePath } from 'react-router'

import type { BaseMakeLessonMaterialFilterService } from '@/contentComponents/makeLessonMaterial/(filter)/service/MakeLessonMaterialFilter.service'
import type { SchoolSubjectGrade } from '@/dto/Student/SchoolSubjectGrade'
import type { RawTreeNode } from '@/models/FlatTree'
import type { SchoolTestWithRegion } from '@/modules/schooTest2/dto/SchoolTestWithRegion.dto'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router/RouteName'
import { sessionStorageService } from '@/service/storage.service'

import type { ConceptChapterCustomWorkbookContext } from '../conceptChapterCustomWorkbook.context'
import type { ConceptStepCustomWorkbookContext } from '../conceptStepCustomWorkbook.context'
import type { SchoolTestStepCustomWorkbookContext } from '../schoolTestStepCustomWorkbook.context'
import type { WorkbookProblemChapterCustomWorkbookContext } from '../workbookProblemChapterCustomWorkbook.context'
import type { WorkbookProblemStepCustomWorkbookContext } from '../workbookProblemStepCustomWorkbook.context'
import type { BaseMakeCustomWorkbookStepOneStore } from './baseMakeCustomWorkbookStepOneStore'
import type {
  BaseMakeCustomWorkbookStepThreeStore,
  FilterChapterT,
} from './baseMakeCustomWorkbookStepThreeStore'
import type { BaseMakeCustomWorkbookStepTwoStore } from './baseMakeCustomWorkbookStepTwoStore'

export type MakeCustomWorkbookContextT<
  ContextType extends WorkbookApi.CustomWorkbookType = WorkbookApi.CustomWorkbookType,
> = ContextType extends 'CONCEPT_CHAPTER'
  ? ConceptChapterCustomWorkbookContext
  : ContextType extends 'CONCEPT_STEP'
    ? ConceptStepCustomWorkbookContext
    : ContextType extends 'WORKBOOK_PROBLEM_CHAPTER'
      ? WorkbookProblemChapterCustomWorkbookContext
      : ContextType extends 'WORKBOOK_PROBLEM_STEP'
        ? WorkbookProblemStepCustomWorkbookContext
        : ContextType extends 'SCHOOL_TEST_PROBLEM_STEP'
          ? SchoolTestStepCustomWorkbookContext
          : never

export type OriginWorkbookType<T extends WorkbookApi.CustomWorkbookType> = T extends
  | 'WORKBOOK_PROBLEM_CHAPTER'
  | 'WORKBOOK_PROBLEM_STEP'
  ? WorkbookEntity.SupportedWorkbook
  : T extends 'SCHOOL_TEST_PROBLEM_STEP'
    ? {
        schoolGrade: Pick<SchoolSubjectGrade, 'schoolType' | 'grade' | 'semester' | 'revision'>
        schoolTestWithRegions: SchoolTestWithRegion[]
      } & WorkbookApi.PostPutCustomWorkbook.SchoolTestPdfOption
    : never

type BuildMode = 'BUILD_MODE_CREATE' | 'BUILD_MODE_EDIT' | 'BUILD_MODE_CLONE_EDIT'
type FromEditPathParams = Record<Exclude<BuildMode, 'BUILD_MODE_CREATE'>, 'edit' | 'copy-edit'>
type ToEditPathParams = Record<ValueOf<FromEditPathParams>, keyof FromEditPathParams>

export const editPathMatch: {
  from: FromEditPathParams
  to: ToEditPathParams
} = {
  from: {
    BUILD_MODE_EDIT: 'edit',
    BUILD_MODE_CLONE_EDIT: 'copy-edit',
  },
  to: {
    edit: 'BUILD_MODE_EDIT',
    'copy-edit': 'BUILD_MODE_CLONE_EDIT',
  },
}

export type MakeCustomWorkbookEditPathParams = {
  buildMode: keyof FromEditPathParams
  workbookId: `${WorkbookEntity.CustomWorkbook['id']}`
}

export const makeCustomWorkbookSearchParamKey = {
  redirectTo: 'redirectTo',
  filterId: 'filterId',
} as const

/**
 * @abstract
 * 교재만들기 컨텍스트
 * 추상클래스로 구현, 각 실제 컨텍스트에서 상속받아 구현 후 사용한다.
 */
export abstract class BaseMakeCustomWorkbookContext<
  ContextType extends WorkbookApi.CustomWorkbookType = WorkbookApi.CustomWorkbookType,
  _OriginWorkbookType extends OriginWorkbookType<ContextType> = OriginWorkbookType<ContextType>,
  StepOneStore extends
    BaseMakeCustomWorkbookStepOneStore<ContextType> = BaseMakeCustomWorkbookStepOneStore<ContextType>,
  StepTwoStore extends
    BaseMakeCustomWorkbookStepTwoStore<ContextType> = BaseMakeCustomWorkbookStepTwoStore<ContextType>,
  StepThreeStore extends
    BaseMakeCustomWorkbookStepThreeStore<ContextType> = BaseMakeCustomWorkbookStepThreeStore<ContextType>,
  FilterService extends BaseMakeLessonMaterialFilterService<
    WorkbookApi.GetProblemCount.Request<ContextType>
  > = BaseMakeLessonMaterialFilterService<WorkbookApi.GetProblemCount.Request<ContextType>>,
> {
  step1: StepOneStore // 1단계에서 사용하는 로직의 묶음
  step2: StepTwoStore // 2단계에서 사용하는 로직의 묶음
  step3: StepThreeStore // 3단계에서 사용하는 로직의 묶음
  filterService: FilterService // 1 ~ 3단계에서 사용하는 필터 로직의 묶음

  originWorkbook?: _OriginWorkbookType // 시중교재나 학교별기출 같이 내부 보유 workbook 컨텐츠로부터 문제를 추출할 경우 originWorkbook의 정보가 1~3단계에서 필요하다.
  customWorkbookToEdit?: WorkbookEntity.CustomWorkbook // 수정, 복제후 수정으로 들어왔을 때 수정할 대상 workbook
  buildMode: BuildMode = 'BUILD_MODE_CREATE' // 생성, 수정, 복제후 수정

  shareScope: ShareScope = 'ACADEMY'

  constructor(
    params: {
      step1: StepOneStore
      step2: StepTwoStore
      step3: StepThreeStore
      filterService: FilterService
    } & (
      | { originWorkbook?: _OriginWorkbookType }
      | {
          originWorkbook?: _OriginWorkbookType
          buildMode: BaseMakeCustomWorkbookContext['buildMode']
          customWorkbookToEdit: WorkbookApi.GetCustomWorkbookById.Response
        }
    ),
  ) {
    this.step1 = params.step1
    this.step2 = params.step2
    this.step3 = params.step3
    this.filterService = params.filterService
    this.originWorkbook = params.originWorkbook

    // 수정, 복제후 수정으로 들어왔을 때에는 수정할 대상 workbook 정보 저장
    if ('buildMode' in params) {
      this.buildMode = params.buildMode
      this.customWorkbookToEdit = new WorkbookEntity.CustomWorkbook(params.customWorkbookToEdit)
      this.step2.showLittleChapterTheory = this.customWorkbookToEdit.showLittleChapterTheory
    }

    // this.saveFilterToSessionStorage = this.saveFilterToSessionStorage.bind(this)
    this.setOriginWorkbook = this.setOriginWorkbook.bind(this)
    this.navigateStepOneToStepTwo = this.navigateStepOneToStepTwo.bind(this)
    this.navigateStepTwoToStepThree = this.navigateStepTwoToStepThree.bind(this)
    this.getAddProblemFilter = this.getAddProblemFilter.bind(this)
    this.getAddFavoriteProblemFilter = this.getAddFavoriteProblemFilter.bind(this)
    this.getAddPairSimilarProblemFilter = this.getAddPairSimilarProblemFilter.bind(this)
    this.setFilter = this.setFilter.bind(this)
    this.navigateStepTwoToStepOne = this.navigateStepTwoToStepOne.bind(this)
    this.fetchSetPostInnerDesignTemplateFilter =
      this.fetchSetPostInnerDesignTemplateFilter.bind(this)
  }

  static observables = {
    step2: observable,
    originWorkbook: observable,
    fetchSetPostInnerDesignTemplateFilter: action,
  }

  // contextType은 필터의 customWorkbookType 값이 Unique하고 해당 컨텍스트를 나타내는 확실한 값이기 때문에 사용한다.
  get contextType(): ContextType {
    return this.filterService.getFilters().customWorkbookType as ContextType
  }

  static MAX_PROBLEM_COUNT = 1000 as const

  // searchParams를 임의로 등록하지 말고, 이것 통해서 관리되는 형태로 사용하기
  static searchParams = {
    set: (
      searchParams: Partial<Record<ValueOf<typeof makeCustomWorkbookSearchParamKey>, string>>,
    ) => {
      const searchParam = new URLSearchParams()
      Object.entries(searchParams).forEach(([key, value]) => {
        searchParam.set(key, value)
      })

      return searchParam.toString()
    },
  }

  // 교재만들기 1단계 진입은 이 static 함수를 호출하는 것으로 일원화한다.
  static enterToCreate(redirectTo?: string) {
    const defaultRedirectPath = commonRepo.router.location.pathname

    const search = BaseMakeCustomWorkbookContext.searchParams.set({
      redirectTo: redirectTo ?? defaultRedirectPath,
    })

    commonRepo.router.push({
      ...commonRepo.router.location,
      pathname: generatePath(RouteName.makeCustomWorkbookStepOne),
      search,
    })
  }

  // 교재만들기 1단계 진입은 이 static 함수를 호출하는 것으로 일원화한다.
  static enterToCreateWorkbookProblem(redirectTo?: string) {
    const defaultRedirectPath = commonRepo.router.location.pathname

    const search = BaseMakeCustomWorkbookContext.searchParams.set({
      redirectTo: redirectTo ?? defaultRedirectPath,
    })
    commonRepo.router.push({
      ...commonRepo.router.location,
      pathname: generatePath(RouteName.makeCustomWorkbookStepOneWorkbookProblem),
      search,
    })
  }

  // 교재만들기 1단계 진입은 이 static 함수를 호출하는 것으로 일원화한다.
  static enterToCreateWorkbookProblemDetail(
    workbookId: WorkbookEntity.SupportedWorkbook['id'],
    redirectTo?: string,
  ) {
    const defaultRedirectPath = commonRepo.router.location.pathname

    const search = BaseMakeCustomWorkbookContext.searchParams.set({
      redirectTo: redirectTo ?? defaultRedirectPath,
    })
    commonRepo.router.push({
      ...commonRepo.router.location,
      pathname: generatePath(RouteName.makeCustomWorkbookStepOneWorkbookProblemDetail, {
        workbookId,
      }),
      search,
    })
  }

  // 교재만들기 1단계 진입은 이 static 함수를 호출하는 것으로 일원화한다.
  static enterToCreateSchoolTest(redirectTo?: string) {
    const defaultRedirectPath = commonRepo.router.location.pathname

    const search = BaseMakeCustomWorkbookContext.searchParams.set({
      redirectTo: redirectTo ?? defaultRedirectPath,
    })
    commonRepo.router.push({
      ...commonRepo.router.location,
      pathname: generatePath(RouteName.makeCustomWorkbookStepOneSchoolTest),
      search,
    })
  }

  // 교재만들기 수정/복제후수정 진입은 모두 static 함수를 호출하는 것으로 일원화한다.
  static async enterToEdit({
    buildMode,
    workbookId,
    redirectTo,
  }: {
    buildMode: keyof ToEditPathParams
    workbookId: WorkbookEntity.CustomWorkbook['id']
    redirectTo?: string
  }) {
    const defaultRedirectPath = commonRepo.router.location.pathname

    const search = BaseMakeCustomWorkbookContext.searchParams.set({
      redirectTo: redirectTo ?? defaultRedirectPath,
    })

    commonRepo.router.push({
      ...commonRepo.router.location,
      pathname: generatePath(RouteName.makeCustomWorkbookStepTwoEdit, {
        buildMode,
        workbookId,
      }),
      search,
    })
  }

  static exitToRedirect() {
    const searchParams = new URLSearchParams(commonRepo.router.location.search)
    const redirectPath = searchParams.get('redirectTo')
    commonRepo.router.replace(redirectPath ?? RouteName.academyShareScopeWorkbook)
  }

  static stepOnePathname = (
    originWorkbookId?: OriginWorkbookType<
      'WORKBOOK_PROBLEM_CHAPTER' | 'WORKBOOK_PROBLEM_STEP'
    >['id'],
  ) => {
    return {
      CONCEPT_CHAPTER: RouteName.makeCustomWorkbookStepOneConcept,
      CONCEPT_STEP: RouteName.makeCustomWorkbookStepOneConcept,
      WORKBOOK_PROBLEM_CHAPTER: originWorkbookId
        ? generatePath(RouteName.makeCustomWorkbookStepOneWorkbookProblemDetail, {
            workbookId: originWorkbookId,
          })
        : RouteName.makeCustomWorkbookStepOneWorkbookProblem,
      WORKBOOK_PROBLEM_STEP: originWorkbookId
        ? generatePath(RouteName.makeCustomWorkbookStepOneWorkbookProblemDetail, {
            workbookId: originWorkbookId,
          })
        : RouteName.makeCustomWorkbookStepOneWorkbookProblem,
      SCHOOL_TEST_PROBLEM_STEP: RouteName.makeCustomWorkbookStepOneSchoolTest,
    }
  }

  setOriginWorkbook = (
    originWorkook: BaseMakeCustomWorkbookContext<
      ContextType,
      _OriginWorkbookType,
      StepOneStore
    >['originWorkbook'],
  ) => {
    this.originWorkbook = originWorkook

    if (this.originWorkbook) {
      sessionStorageService.removeItem('MAKE_CUSTOM_WORKBOOK_ORIGIN_WORKBOOK')
      sessionStorageService.setItem('MAKE_CUSTOM_WORKBOOK_ORIGIN_WORKBOOK')({
        [this.filterService.filterId]: this.originWorkbook,
      })
    }
  }

  setFilter = (data?: Partial<WorkbookApi.GetProblemCount.Request<ContextType>>) => {
    this.filterService.set(data)
    sessionStorageService.removeItem('MAKE_CUSTOM_WORKBOOK_FILTER')

    const save = sessionStorageService.setItem('MAKE_CUSTOM_WORKBOOK_FILTER')
    const filter = this.filterService.getAll()
    save({ [filter.filterId]: filter })
    return this.filterService.getFilters()
  }

  // 1단계에서 2단계로 진입
  navigateStepOneToStepTwo() {
    this.setFilter()

    if (this.step1.chapterTree) {
      this.step2.setChapterTreeData(this.step1.chapterTree)
    }

    if (this.filterService.filterId) {
      const search = BaseMakeCustomWorkbookContext.searchParams.set({
        filterId: this.filterService.filterId,
      })

      commonRepo.router.push({
        ...commonRepo.router.location,
        pathname: generatePath(RouteName.makeCustomWorkbookStepTwoCreate),
        search,
      })
    }
  }

  // 2단계에서 3단계로 진입
  navigateStepTwoToStepThree(problemCount: number) {
    this.filterService.set()
    this.step2.setSelectedPairSimilarProblem(null)
    this.step2.tab = 'SUMMARY'
    this.step2.subTab = 'WORKBOOK_INFO'
    this.step3.problemCount = problemCount

    if (this.filterService.filterId) {
      const search = BaseMakeCustomWorkbookContext.searchParams.set({
        filterId: this.filterService.filterId,
      })

      commonRepo.router.push({
        ...commonRepo.router.location,
        pathname: this.customWorkbookToEdit
          ? generatePath(RouteName.makeCustomWorkbookStepThreeEdit, {
              buildMode: this.buildMode,
              workbookId: this.customWorkbookToEdit?.id,
            })
          : generatePath(RouteName.makeCustomWorkbookStepThreeCreate),
        search,
      })
    }
  }

  // 2단계에서 1단계로 진입
  navigateStepTwoToStepOne() {
    this.filterService.set()

    if (this.filterService.filterId) {
      const search = BaseMakeCustomWorkbookContext.searchParams.set({
        filterId: this.filterService.filterId,
      })

      this.step2 = this.step2.create() as StepTwoStore

      const filters = this.filterService.getFilters()

      const originSupportedWorkbookId =
        filters.customWorkbookType === 'WORKBOOK_PROBLEM_CHAPTER' ||
        filters.customWorkbookType === 'WORKBOOK_PROBLEM_STEP'
          ? filters.originSupportedWorkbookId
          : undefined

      commonRepo.router.replace({
        ...commonRepo.router.location,
        pathname:
          BaseMakeCustomWorkbookContext.stepOnePathname(originSupportedWorkbookId)[
            this.contextType
          ],
        search,
      })
    }
  }

  // 3단계에서 동일옵션으로 만들기 체크되어 1단계 재진입 시
  navigateStepThreeToStepOne() {
    const filters = this.filterService.getFilters()

    this.step2 = this.step2.create() as StepTwoStore
    this.step1.chapterTree = undefined
    this.step1.fetchSetChapterTree(this.filterService.getFilters())

    commonRepo.router.replace({
      pathname:
        filters.customWorkbookType === 'CONCEPT_CHAPTER' ||
        filters.customWorkbookType === 'CONCEPT_STEP'
          ? RouteName.makeCustomWorkbookStepOneConcept
          : filters.customWorkbookType === 'WORKBOOK_PROBLEM_CHAPTER' ||
              filters.customWorkbookType === 'WORKBOOK_PROBLEM_STEP'
            ? generatePath(RouteName.makeCustomWorkbookStepOneWorkbookProblemDetail, {
                workbookId: filters.originSupportedWorkbookId,
              })
            : RouteName.makeCustomWorkbookStepOneSchoolTest,
      search: commonRepo.router.location.search,
    })
  }

  abstract getAddProblemFilter(): ProblemApi.GetRecommendedProblemsByConcept.Request | undefined

  abstract getAddFavoriteProblemFilter(): ProblemApi.PostFavorites.Request | undefined

  abstract getAddPairSimilarProblemFilter():
    | ProblemApi.GetRecommendedProblemsByProblem.Request
    | undefined

  abstract getPostCustomWorkbookFilter<
    U extends ContextType extends 'CONCEPT_CHAPTER' | 'WORKBOOK_PROBLEM_CHAPTER'
      ? 'LITTLE_CHAPTER' | 'MIDDLE_CHAPTER' // CHAPTER일 경우 소단원인지 중단원인지
      : never = never,
  >(): FilterChapterT<ContextType, U>

  abstract fetchSetPostInnerDesignTemplateFilter(): Promise<void>
}

// TODO: 이름 다시 지어야 됨
export type RawMakeProblemT = RawTreeNode<WorkbookApi.GetProblems.Problem>

// 일부러 강하게 타입 지정하지 않음
export type RawMakeChapterT = RawTreeNode<{
  id: number
  name: string
  type:
    | 'BIG_CHAPTER'
    | 'MIDDLE_CHAPTER'
    | 'LITTLE_CHAPTER'
    | 'CONCEPT'
    | 'PRACTICE_TEST_ROUND'
    | 'PROBLEM'
  step?:
    | 'THEORY'
    | 'EXERCISE'
    | 'CHALLENGE'
    | 'WRAP_UP'
    | 'PAIR_SIMILAR'
    | 'SCHOOL_WORKBOOK'
    | 'PRACTICE_TEST'
    | null
  imageUrl?: string // CONCEPT 일 때
  bigChapterName?: string
  middleChapterName?: string
  littleChapters?: { id: number; name: string; url: string }[]
  // children은 RawMakeChapterT[] 또는 RawMakeProblemT[] (마지막 depth)
  children?: RawMakeChapterT[] | RawMakeProblemT[]
}>
